import React from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import CircularProgress from "@material-ui/core/CircularProgress";
import styled from "styled-components";
import { MUST_VALIDATE_FIELDS } from "../../../../app/contexts/constants/study.context.constants";
import { useSnackbar } from "../../../../app/contexts/snackbar.context";
import { useStudyPlan } from "../../../../app/contexts/study.context";
import PrimaryButton from "../../../../app/components/buttons/primary";
import OutlinedButton from "../../../../app/components/buttons/outlined";
import { validateSeasonalVariations } from "../../validations/validate.study.plan";
import { getSitesAndSubjectsParameters } from "../../utils/helpers";

const BottomControls = ({ errors, fieldErrors, warnings }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id, planId } = useParams();
  const { pathname } = location;
  const { showSnackbar } = useSnackbar();
  const {
    studyPlan,
    savePlan,
    saveLoading,
    unsavedChange,
    validateSequentialCohort,
    hideEnrollmentDuration,
    isSequential,
  } = useStudyPlan();
  const {
    studyPlanName,
    croProjectStartDateMillis,
    studyPlanDescr,
    finalProtocolStartDateMillis,
    treatmentDuration,
    screeningPeriodDuration,
    overallEnrollmentDuration,
  } = studyPlan;
  const [sites_to_activate, subjects_to_screen] = getSitesAndSubjectsParameters(studyPlan?.parameters);

  const pushToNextPage = () => {
    const seqValid = isSequential
      ? validateSequentialCohort(location.pathname)
      : true;

    const { seasonalValid } = validateSeasonalVariations(studyPlan);
    if (Object.keys(warnings || {}).length !== 0) {
      showSnackbar(
        <ul>
          {Object.keys(warnings || {}).map((key) => {
            if (key !== "BUTTON_TYPE") return <li>{warnings[key]}</li>;
            return null;
          })}
        </ul>,
        "warning",
      );
    }
    if ((Object.keys(errors || {}).length !== 0 &&
        ["ALL"].includes(errors.BUTTON_TYPE)) ||
      !seqValid ||
      !seasonalValid
    ) {
      showSnackbar(
        <ul>
          {Object.keys(errors || {}).map((key) => {
            if (key === "ENROLLMENT_DURATION") return "";
            if (key !== "BUTTON_TYPE") return <li>{errors[key]}</li>;
            return "";
          })}
          {!seqValid && <li>Sequential cohorts setup is not valid</li>}
          {!seasonalValid && (
            <li>Country enrollment seasonal variations are not valid</li>
          )}
        </ul>,
        "error",
      );
      return;
    }
    if (pathname.includes("new-plan")) {
      savePlan({
        isNew: true,
        redirect: (newPlanId) => {
          navigate(
            `/ctpo/study-plans/${id}/${newPlanId}/countries-and-site-groups`,
          );
        },
      });
    } else if (unsavedChange) {
      savePlan({
        isNew: false,
        redirect: () =>
          navigate(
            `/ctpo/study-plans/${id}/${planId}/countries-and-site-groups`,
          ),
      });
    } else {
      navigate(
        `/ctpo/study-plans/${id}/${planId}/countries-and-site-groups`,
      );
    }
  };

  const handleDisabled = () => {
    const hasForbiddenErrors = MUST_VALIDATE_FIELDS.some(key => errors?.[key]);
    if (hasForbiddenErrors) return true;

    const hasFieldErrors = Object.values(fieldErrors).some(
      (field) => field.errors && field.errors.length > 0,
    );
    return (
      hasFieldErrors ||
      !studyPlanName ||
      !croProjectStartDateMillis ||
      !studyPlanDescr ||
      !finalProtocolStartDateMillis ||
      sites_to_activate.lowerBound === null ||
      !(sites_to_activate.lowerBound > 0) ||
      subjects_to_screen.lowerBound === null ||
      !(subjects_to_screen.lowerBound > 0) ||
      treatmentDuration === null ||
      !(treatmentDuration > 0) ||
      screeningPeriodDuration === null ||
      !(screeningPeriodDuration >= 0) ||
      (!hideEnrollmentDuration && (overallEnrollmentDuration === null || !(overallEnrollmentDuration >= 0)))
    );
  };

  return (
    <Container>
      <OutlinedButton disabled>Back</OutlinedButton>
      <PrimaryButton
        onClick={pushToNextPage}
        disabled={handleDisabled() || saveLoading}
      >
        {saveLoading ? (
          <CircularProgress
            style={{ color: "var(--White)" }}
            size="1em"
          />
        ) : (
          "Next Step"
        )}
      </PrimaryButton>
    </Container>
  );
};

export default BottomControls;

const Container = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1em;
  margin-bottom: 1em;
`;
